import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { addDocument, findDocumentsByField } from '../services/firestoreService';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  LinearProgress,
  Input,
} from '@mui/material';

const ProcedureImporter = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [progress, setProgress] = useState(0);

  const handleFileChange = (e) => {
    setError('');
    setSuccess('');
    setProgress(0);
    setFile(e.target.files?.[0] || null);
  };

  const getValueByCandidates = (row, candidates) => {
    const keys = Object.keys(row || {});
    for (const candidate of candidates) {
      const foundKey = keys.find((k) => k.trim().toLowerCase() === candidate.toLowerCase());
      if (foundKey) return row[foundKey];
    }
    return '';
  };

  const normalizeBoolean = (value) => {
    if (!value) return false;
    const str = String(value).trim().toLowerCase();
    return str === 'sí' || str === 'si' || str === 'yes' || str === 'true' || str === '1';
  };

  const importRows = async (rows) => {
    if (!rows || rows.length === 0) {
      setError('El archivo no contiene filas de datos.');
      setLoading(false);
      return;
    }

    let imported = 0;
    let duplicates = 0;

    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      try {
        const grupo = getValueByCandidates(row, ['Grupo', 'grupo']);
        const codigo = getValueByCandidates(row, ['Código (ID)', 'Código', 'Codigo', 'ID', 'id', 'código', 'codigo']);
        const procedimiento = getValueByCandidates(row, ['Procedimiento', 'procedimiento']);
        const pabellonRaw = getValueByCandidates(row, ['Pabellón', 'Pabellon', 'pabellón', 'pabellon']);
        const lugar = getValueByCandidates(row, ['Lugar', 'lugar']);
        const nivelAnestesia = getValueByCandidates(row, ['Nivel de Anestesia', 'Nivel Anestesia', 'Anestesia', 'anestesia']);

        // DEBUG: log para ver qué se extrae de cada fila
        console.log(`Fila ${i + 1}:`, { grupo, codigo, procedimiento, pabellonRaw, lugar, nivelAnestesia, rawRow: row });

        if (!grupo && !codigo && !procedimiento) {
          // Fila completamente vacía: la saltamos
          console.log(`Fila ${i + 1} vacía, omitiendo.`);
          setProgress(Math.round(((i + 1) / rows.length) * 100));
          continue;
        }

        if (!codigo) {
          // Sin código no podemos identificar el procedimiento: lo omitimos
          console.log(`Fila ${i + 1} sin código, omitiendo.`);
          setProgress(Math.round(((i + 1) / rows.length) * 100));
          continue;
        }

        // Evitar duplicados por código (ID)
        const existing = await findDocumentsByField('procedures', 'code', codigo);
        if (existing && existing.length > 0) {
          console.log(`Fila ${i + 1}: código "${codigo}" ya existe, omitiendo por duplicado.`);
          duplicates += 1;
          setProgress(Math.round(((i + 1) / rows.length) * 100));
          continue;
        }

        const doc = {
          group: String(grupo || '').trim(),
          code: String(codigo || '').trim(),
          procedure: String(procedimiento || '').trim(),
          pabellon: normalizeBoolean(pabellonRaw),
          location: String(lugar || '').trim(),
          anesthesiaLevel: String(nivelAnestesia || '').trim(),
          createdAt: new Date(),
        };

        await addDocument('procedures', doc);
        imported += 1;
        setProgress(Math.round(((i + 1) / rows.length) * 100));
      } catch (err) {
        console.error('Error procesando fila de procedimientos:', row, err);
        setError(`Error al procesar la fila ${i + 1}. La importación se ha detenido.`);
        setLoading(false);
        return;
      }
    }

    const duplicateInfo = duplicates
      ? ` (${duplicates} filas omitidas por código duplicado)`
      : '';
    setSuccess(`${imported} de ${rows.length} procedimientos importados con éxito.${duplicateInfo}`);
    setLoading(false);
  };

  const processImport = async () => {
    if (!file) {
      setError('Por favor, selecciona un archivo Excel o CSV.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setProgress(0);

    const fileName = file.name || '';
    const ext = fileName.split('.').pop()?.toLowerCase() || '';

    if (ext === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          const rows = results.data || [];
          await importRows(rows);
        },
        error: (err) => {
          console.error('Error al leer el archivo CSV de procedimientos:', err);
          setError('Error al leer el archivo CSV: ' + err.message);
          setLoading(false);
        },
      });
      return;
    }

    if (ext === 'xlsx' || ext === 'xls') {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
          await importRows(rows);
        } catch (err) {
          console.error('Error al procesar el archivo Excel de procedimientos:', err);
          setError('Error al procesar el archivo Excel. Revisa el formato.');
          setLoading(false);
        }
      };
      reader.onerror = (err) => {
        console.error('Error de lectura del archivo Excel:', err);
        setError('No se pudo leer el archivo Excel.');
        setLoading(false);
      };
      reader.readAsArrayBuffer(file);
      return;
    }

    setError('Formato de archivo no soportado. Usa .xlsx, .xls o .csv.');
    setLoading(false);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Importar Procedimientos desde Excel
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        El archivo debe tener seis columnas: <strong>Grupo</strong>, <strong>Código (ID)</strong>, <strong>Procedimiento</strong>, <strong>Pabellón</strong>, <strong>Lugar</strong> y <strong>Nivel de Anestesia</strong>.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Importante:</strong> El campo <strong>Código (ID)</strong> debe ser único para cada procedimiento. En <strong>Pabellón</strong> usa valores como "sí", "no", "true" o "false".
      </Alert>
      <Box>
        <Input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileChange}
          sx={{ mb: 2 }}
        />
        <Button
          variant="contained"
          onClick={processImport}
          disabled={loading || !file}
        >
          {loading ? 'Importando...' : 'Importar Procedimientos'}
        </Button>
      </Box>
      {loading && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="body2" color="text.secondary">{`${progress}%`}</Typography>
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {success}
        </Alert>
      )}
    </Paper>
  );
};

export default ProcedureImporter;
